import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { getNotificationCardBg, getNotificationCardBorder, getNotificationCardHeading, getNotificationCardText } from '../../../utils/statusColors';
import { TokenManagement } from './TokenManagement';
import { BarChart3, Coins } from 'lucide-react';

interface UsageByFeature {
  _id: string;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  requestCount: number;
  totalCost: number;
}

interface UsageData {
  byFeature: UsageByFeature[];
  total: {
    totalTokens: number;
    totalRequests: number;
    totalCost: number;
  };
}

type TabType = 'tokens' | 'usage';

export const UsageDashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('tokens');
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchUsage = async () => {
    try {
      setLoading(true);
      
      const now = new Date();
      let startDate: Date | undefined;
      
      switch (dateRange) {
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = undefined;
      }

      const token = localStorage.getItem('token');
      const url = startDate
        ? `${apiUrl}/api/ai-usage/account?startDate=${startDate.toISOString()}`
        : `${apiUrl}/api/ai-usage/account`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setUsageData(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error('Fetch usage error:', err);
      setError(t('admin.failedToLoadUsageData'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, [dateRange]);

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const formatCost = (cents: number): string => {
    if (!cents) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getFeatureName = (feature: string): string => {
    const names: Record<string, string> = {
      'chat': t('admin.aiChat') || 'AI Chat',
      'bill_extraction': t('admin.billExtraction') || 'Bill Extraction',
      'quote_generation': t('admin.quoteGeneration') || 'Quote Generation',
      'document_analysis': t('admin.documentAnalysis') || 'Document Analysis',
      'transcription': t('admin.transcription') || 'Transcription',
      'project_plan': t('admin.projectPlanning') || 'Project Planning'
    };
    return names[feature] || feature;
  };

  return (
    <>
      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-600">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('tokens')}
            style={{ outline: "none", boxShadow: "none" }}
            className={`flex items-center space-x-2 pb-4 px-1 border-b-2 font-medium text-sm transition-colors [&:focus]:outline-none [&:focus]:ring-0 [&:focus]:shadow-none [&:active]:outline-none ${
              activeTab === 'tokens'
                ? "border-primary-500 text-primary-600 dark:text-primary-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            <Coins className="w-5 h-5" />
            <span>Token Management</span>
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            style={{ outline: "none", boxShadow: "none" }}
            className={`flex items-center space-x-2 pb-4 px-1 border-b-2 font-medium text-sm transition-colors [&:focus]:outline-none [&:focus]:ring-0 [&:focus]:shadow-none [&:active]:outline-none ${
              activeTab === 'usage'
                ? "border-primary-500 text-primary-600 dark:text-primary-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            <span>AI Usage Analytics</span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">

      {activeTab === 'tokens' ? (
        <TokenManagement />
      ) : (
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-lg text-gray-600 dark:text-gray-400">{t('admin.loadingUsageData')}</div>
            </div>
          ) : error || !usageData ? (
            <div className="bg-red-50 dark:bg-error-900/40 border border-red-200 dark:border-error-700/50 rounded-lg p-4">
              <p className="text-error-800 dark:text-error-300">{error || t('admin.failedToLoadUsageData')}</p>
            </div>
          ) : (
            <>
              {/* Header with Date Range Selector */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.aiUsageDashboard')}</h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setDateRange('7d')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                dateRange === '7d'
                  ? 'bg-accent-600 dark:bg-accent-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('admin.sevenDays')}
            </button>
            <button
              onClick={() => setDateRange('30d')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                dateRange === '30d'
                  ? 'bg-accent-600 dark:bg-accent-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('admin.thirtyDays')}
            </button>
            <button
              onClick={() => setDateRange('90d')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                dateRange === '90d'
                  ? 'bg-accent-600 dark:bg-accent-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('admin.ninetyDays')}
            </button>
            <button
              onClick={() => setDateRange('all')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                dateRange === 'all'
                  ? 'bg-accent-600 dark:bg-accent-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('admin.allTime')}
            </button>
          </div>
        </div>

        {/* Total Usage Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`${getNotificationCardBg("info")} ${getNotificationCardBorder("info")} border rounded-lg p-4`}>
            <h3 className={`text-sm font-medium mb-2 ${getNotificationCardHeading("info")}`}>{t('admin.totalTokens')}</h3>
            <p className={`text-3xl font-bold ${getNotificationCardText("info")}`}>
              {formatNumber(usageData.total.totalTokens || 0)}
            </p>
          </div>
          <div className={`${getNotificationCardBg("success")} ${getNotificationCardBorder("success")} border rounded-lg p-4`}>
            <h3 className={`text-sm font-medium mb-2 ${getNotificationCardHeading("success")}`}>{t('admin.totalRequests')}</h3>
            <p className={`text-3xl font-bold ${getNotificationCardText("success")}`}>
              {formatNumber(usageData.total.totalRequests || 0)}
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-300 dark:border-purple-700/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-purple-700 dark:text-purple-400 mb-2">{t('admin.estimatedCost')}</h3>
            <p className="text-3xl font-bold text-purple-900 dark:text-purple-100">
              {formatCost(usageData.total.totalCost || 0)}
            </p>
          </div>
              </div>
            </div>

            {/* Usage by Feature */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('admin.usageByFeature')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('admin.feature')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('admin.requests')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('admin.tokens')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('admin.estCost')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
              {usageData.byFeature.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    {t('admin.noUsageData')}
                  </td>
                </tr>
              ) : (
                usageData.byFeature.map((feature) => (
                  <tr key={feature._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {getFeatureName(feature._id)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                      {formatNumber(feature.requestCount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                      {formatNumber(feature.totalTokens)}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatNumber(feature.promptTokens)} {t('admin.prompt')} + {formatNumber(feature.completionTokens)} {t('admin.completion')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                      {formatCost(feature.totalCost)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
              </table>
            </div>
          </div>

          {/* Information Box */}
          <div className="bg-accent-50 dark:bg-gray-700 border border-accent-200 dark:border-gray-600 rounded-lg p-4">
            <h3 className="text-sm font-medium text-accent-900 dark:text-gray-100 mb-2">{t('admin.aboutAiUsageTracking')}</h3>
            <ul className="text-sm text-accent-800 dark:text-gray-300 space-y-1 list-disc list-inside">
              <li>{t('admin.tokenUsageTracked')}</li>
              <li>{t('admin.costsEstimated')}</li>
              <li>{t('admin.freePlanBillExtractions')}</li>
              <li>{t('admin.premiumPlanUnlimited')}</li>
            </ul>
          </div>
        </>
        )}
        </div>
      )}
      </div>
    </>
  );
};
